import React, { useState } from "react";
import HeaderName from "../../../components/Custom/HeaderName";
import {
    Box,
    Button,
    CircularProgress,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router";
import { InputBox } from "../../../components/Custom";
import { H4FormLabel } from "../../../theme/Theme";
import ThemeColor from "../../../theme/ThemeColor";
import { fetchAndOpenDocketPdf, trackDocketId } from "../../../redux/Thunk/docketThunk";
import { useDispatch } from "react-redux";
import Timeline from "@mui/lab/Timeline";
import TimelineItem from "@mui/lab/TimelineItem";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import TimelineConnector from "@mui/lab/TimelineConnector";
import TimelineContent from "@mui/lab/TimelineContent";
import TimelineOppositeContent from "@mui/lab/TimelineOppositeContent";
import TimelineDot from "@mui/lab/TimelineDot";
import DisplayError from "../../../validation/DisplayError";
import validate from "../../../validation/validation";
import Swal from "sweetalert2";
import moment from "moment";

const DocketTracking = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { state } = useLocation();

    const [loader, setLoader] = useState(false);
    const [openTimeLine, setOpenTimeLine] = useState(false);
    const [docketId, setDocketId] = useState("");
    const [trackingDetails, setTrackingDetails] = useState([]);
    const [docketData, setDocketData] = useState(null);
    const [error, setError] = useState([]);

    const validation_input = [
        {
            tagid: "docketId",
            text: docketId ?? "",
            required: true,
            regex_name: "free_text",
            errmsg: "Please Enter Docket Id.",
            min: 1,
            max: 50,
        },
    ];

    const handleClickDocketId = () => {
        if (validate(validation_input)?.length === 0) {
            setLoader(true);
            dispatch(trackDocketId(docketId)).then((res) => {
                if (res.payload.success === false) {
                    sweetalert("error", res?.payload?.message);
                } else {
                    setOpenTimeLine(true);
                    setTrackingDetails(res?.payload?.data?.statusHistory);
                    setDocketData(res?.payload?.data);
                }
                setLoader(false);
            });
        } else {
            setError(validate(validation_input));
        }
    };
    const handleDownloadClick = () => {
        console.log("DocketId:", docketId)
        dispatch(fetchAndOpenDocketPdf(docketId));
    };

    const sweetalert = (variant, text) => {
        Swal.fire({
            icon: variant === "error" ? "error" : "success",
            title: text,
            button: "Ok!",
        });
    };

    const pickup = docketData?.pickupDetails;
    const customer = pickup?.Consigner?.[0];
    const consignee = pickup?.Consignee?.[0];

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                flexGrow: 1,
                height: "100vh",
                overflowY: "auto",
                overflowX: "hidden",
                paddingBottom: 3,
            }}
        >

            <HeaderName
                headerName={"Docket Tracking"}
                newForm={false}
                onClickBackButton={() => navigate("/dashboard/docketlist")}
            />

            <Box
                sx={{
                    backgroundColor: "#FFFFFF",
                    mx: 3,
                    my: 2,
                    p: 3,
                    borderRadius: "20px",
                    boxShadow: 3,
                    maxHeight: "calc(100vh - 200px)", // Leaves space for header/back
                    overflowY: "auto",
                    overflowX: "hidden",
                }}
            >

                {/* Input Section */}
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <H4FormLabel sx={{ color: ThemeColor.palette.InputTextColor }}>
                            Docket Id<span style={{ color: "#FF0000" }}>*</span>
                        </H4FormLabel>
                        <InputBox
                            placeholder="Enter Docket Id"
                            name="docketId"
                            value={docketId}
                            onChange={(e) => setDocketId(e.target.value)}
                            sx={{
                                borderRadius: "10px",
                                backgroundColor: "#FFFFFF",
                                color: ThemeColor.palette.InputTextColor,
                                border: "1px solid rgba(0, 0, 0, 0.3)",
                                fontSize: "14px",
                                width: "100%",
                                p: 0.1,
                                pl: 2,
                            }}
                        />
                        <DisplayError error={error} fortagid="docketId" />
                    </Grid>

                    {/* Track Button */}
                    <Grid
                        item
                        xs={12}
                        sm={4}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: 'space-between',
                            width: '70%',
                            mt: 2,
                            gap: 2, // spacing between buttons
                        }}
                    >
                        <Button
                            variant="contained"
                            onClick={handleClickDocketId}
                            sx={{
                                backgroundColor: "#04AB44",
                                color: "#fff",
                                borderRadius: "10px",
                                px: 5,
                                py: 1,
                                fontSize: "16px",
                                fontWeight: 500,
                                "&:hover": { backgroundColor: "#049d3f" },
                            }}
                        >
                            {loader ? <CircularProgress size={20} color="inherit" /> : "Track"}
                        </Button>
                        {openTimeLine && (
                            <Button
                                variant="contained"
                                onClick={handleDownloadClick}
                                sx={{
                                    backgroundColor: "#04AB44",
                                    color: "#fff",
                                    borderRadius: "10px",
                                    px: 5,
                                    py: 1,
                                    fontSize: "16px",
                                    fontWeight: 500,
                                    "&:hover": { backgroundColor: "#049d3f" },
                                }}
                            >
                                Download Docket
                            </Button>
                        )}
                    </Grid>
                </Grid>

                {/* Shipment Summary */}
                {docketData && (
                    <Box sx={{ mt: 4 }}>
                        <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>
                            Shipment Summary
                        </Typography>

                        <Grid container spacing={1} direction="column">
                            {/* Block 1 */}
                            <Grid item xs={12}>
                                <Box sx={blockBoxStyle}>
                                    <Grid container spacing={30}>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <Typography><strong>AWB No:</strong> {docketData?.DocketId || "-"}</Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <Typography><strong>Booking Date:</strong> {moment(pickup?.PickupDateAndTime).format("DD-MMM-YYYY")}</Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <Typography><strong>Service Type:</strong> Surface {pickup?.ServiceType}</Typography>
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Grid>

                            {/* Block 2 */}
                            <Grid item xs={12}>
                                <Box sx={blockBoxStyle}>
                                    <Grid container spacing={20}>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <Typography><strong>Origin:</strong> {pickup?.Origin}</Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <Typography><strong>Destination:</strong> {pickup?.Destination}</Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <Typography><strong>Invoice No:</strong> -- {pickup?.InvoiceNo}</Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <Typography><strong>Status:</strong> {docketData?.Status}</Typography>
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Grid>

                            {/* Block 3 */}
                            <Grid item xs={12}>
                                <Box sx={blockBoxStyle}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Typography><strong>Consignor:</strong> {pickup?.Consignee}</Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Typography><strong>Mobile:</strong> {pickup?.SenderMobile}</Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Typography><strong>Address:</strong> {pickup?.PickupAddress}</Typography>
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Grid>

                            {/* Block 4 */}
                            <Grid item xs={12}>
                                <Box sx={blockBoxStyle}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Typography><strong>Consignee:</strong> {pickup?.Consigner}</Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Typography><strong>Mobile:</strong> {pickup?.ReceiverMobile}</Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Typography><strong>Address:</strong> {pickup?.DestinationAddress}</Typography>
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Grid>
                            <TableContainer component={Paper} sx={{ mt: 1 }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow >
                                            <TableCell sx={{ fontWeight: 'Bold' }}>No. of PCS</TableCell>
                                            <TableCell sx={{ fontWeight: 'Bold' }}>Packing</TableCell>
                                            <TableCell sx={{ fontWeight: 'Bold' }}>CONTENTS (SAID TO CONTAIN)</TableCell>
                                            <TableCell sx={{ fontWeight: 'Bold' }}>DIM (cm) L*W*H</TableCell>
                                            <TableCell sx={{ fontWeight: 'Bold' }}>INVOICE NO</TableCell>
                                            <TableCell sx={{ fontWeight: 'Bold' }}>INVOICE VALUE</TableCell>
                                            <TableCell sx={{ fontWeight: 'Bold' }}>ACTUAL WEIGHT</TableCell>
                                            <TableCell sx={{ fontWeight: 'Bold' }}>Special Remarks</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {pickup?.PackageDetails?.map((pkg, index) => (
                                            <TableRow key={index}>
                                                <TableCell>{index + 1}</TableCell>
                                                <TableCell>{pkg?.BoxType}</TableCell>
                                                <TableCell>{pickup?.Content}</TableCell>
                                                <TableCell>{`${pkg?.length}* ${pkg?.breadth}* ${pkg?.height}`}</TableCell>
                                                <TableCell>---</TableCell>
                                                <TableCell>---</TableCell>
                                                <TableCell>{pkg?.Volumetrix}</TableCell>
                                                <TableCell>{pickup?.Remark}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TableContainer component={Paper} sx={{ mt: 1 }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow >
                                            <TableCell sx={{ fontWeight: 'Bold' }}>Cash Amount</TableCell>
                                            <TableCell sx={{ fontWeight: 'Bold' }}>ToPay Amount</TableCell>
                                            <TableCell sx={{ fontWeight: 'Bold' }}>COD Amount</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {pickup?.PackageDetails?.map((pkg, index) => (
                                            <TableRow key={index}>
                                                <TableCell> -- </TableCell>
                                                <TableCell> -- </TableCell>
                                                <TableCell> --  </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Grid>
                    </Box>
                )}

                {/* Tracking History Table (Like Screenshot) */}
                {openTimeLine && trackingDetails?.length > 0 && (
                    <TableContainer component={Paper} sx={{ mt: 4, border: '1px solid black' }}>
                        <Typography variant="h6" sx={{ fontWeight: "bold", p: 2, }}>
                            Tracking History
                        </Typography>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: "bold" }}>Status Date</TableCell>
                                    <TableCell sx={{ fontWeight: "bold" }}>Time</TableCell>
                                    <TableCell sx={{ fontWeight: "bold" }}>Branch</TableCell>
                                    <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {trackingDetails.map((item, index) => {
                                    const dateTime = moment(item?.UpdatedAt);
                                    const statusDate = dateTime.format("DD-MMM-YYYY");
                                    const time = dateTime.format("hh:mm A");
                                    const branch = item?.Location || "-";
                                    const status = item?.Status || "-";

                                    return (
                                        <TableRow key={index}>
                                            <TableCell>{statusDate}</TableCell>
                                            <TableCell>{time}</TableCell>
                                            <TableCell>{branch}</TableCell>
                                            <TableCell>{status}</TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

            </Box>
        </Box>
    );
};

// Reusable style for each block box
const blockBoxStyle = {
    p: 2,
    borderRadius: 2,
    border: "1px solid #ccc",
    boxShadow: 1,
    backgroundColor: "#fff",
    minHeight: 30,
};

export default DocketTracking;
